import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Tabs, Tab,
    Box, Stack, Grid, Typography, TextField, Checkbox
} from '@mui/material';

// --- HELPER FOR FORM ROWS ---
const ModalFormRow = ({ label, children, bold = false, note = "" }) => (
    <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
        <Grid item xs={12} md={5}>
            <Typography variant="subtitle2" sx={{ fontWeight: bold ? 'bold' : 'normal', display: 'block' }}>
                {label}
            </Typography>
            {note && (
                <Typography variant="caption" sx={{ fontStyle: 'italic', color: '#666', display: 'block', fontSize: '0.75rem' }}>
                    {note}
                </Typography>
            )}
        </Grid>
        <Grid item xs={12} md={7}>{children}</Grid>
    </Grid>
);

export default function AddLiabilityDialog({ open, onClose, matterId, onSave, initialData }) {
    const [tabValue, setTabValue] = useState(0);
    const [formData, setFormData] = useState(initialData || {});

    useEffect(() => {
        if (open) setFormData(initialData || {});
    }, [open, initialData]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSave = async () => {
        try {
            await axios.post(`https://demo.insollex.com/api/legal-matter/${matterId}/liabilities`, formData);
            if (onSave) onSave();
            onClose();
        } catch (error) {
            console.error("Error saving liability:", error);
            alert("Failed to save liability.");
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 1 } }}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: '#4caf50', mr: 1 }} />
                Adding Claim (New)
            </DialogTitle>

            <DialogContent sx={{ p: 0, bgcolor: '#fdfdfd' }}>
                <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ bgcolor: '#f0f0f0', minHeight: 32 }}>
                    <Tab label="Details of Claims" sx={{ fontSize: '0.75rem', textTransform: 'none' }} />
                    <Tab label="L&D Notes/Other" sx={{ fontSize: '0.75rem', textTransform: 'none' }} />
                    <Tab label="Estate Duty" sx={{ fontSize: '0.75rem', textTransform: 'none' }} />
                </Tabs>

                <Box sx={{ p: 3, minHeight: '500px' }}>
                    {/* ================= TAB 1: DETAILS OF CLAIMS ================= */}
                    {tabValue === 0 && (
                        <Stack spacing={0.5}>
                            <ModalFormRow label="Ref./Account Number and Description of Claim">
                                <TextField 
                                    name="description"
                                    onChange={handleChange}
                                    fullWidth 
                                    size="small" 
                                    sx={{ bgcolor: '#fffde7' }} // Matches image_053de6 yellow highlight
                                />
                            </ModalFormRow>

                            <ModalFormRow label="Name of Holder of Claim (e.g. name of bank)">
                                <TextField name="holderName" onChange={handleChange} fullWidth size="small" />
                            </ModalFormRow>

                            <ModalFormRow label="Address of Holder of Claim">
                                <TextField name="holderAddress" onChange={handleChange} fullWidth multiline rows={2} size="small" />
                            </ModalFormRow>

                            <ModalFormRow label="L&D a/c value (e.g. Cert of Balance Capital)" bold>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <TextField name="ldValue" onChange={handleChange} defaultValue="0.00" size="small" sx={{ width: '120px', '& .MuiInputBase-input': { textAlign: 'right' } }} />
                                    <Typography variant="caption">Value approved by SARS for Duty</Typography>
                                    <TextField name="sarsValue" onChange={handleChange} defaultValue="0.00" size="small" sx={{ width: '120px', '& .MuiInputBase-input': { textAlign: 'right' } }} />
                                </Stack>
                            </ModalFormRow>

                            <ModalFormRow label="Is a cashbook required for Inc & Exp after death?">
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Checkbox name="cashbookRequired" onChange={handleChange} size="small" sx={{ p: 0 }} />
                                    <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                                        (Only tick this option if there will be movement on this a/c after death)
                                    </Typography>
                                </Stack>
                            </ModalFormRow>

                            <ModalFormRow label="If debt taken over, by whom?">
                                <Stack direction="row" spacing={1}>
                                    <TextField name="debtTakenOverBy" onChange={handleChange} fullWidth size="small" />
                                    <Button variant="contained" size="small" sx={{ bgcolor: '#e0e0e0', color: '#000', border: '1px solid #ccc', textTransform: 'none' }}>Heirs</Button>
                                </Stack>
                            </ModalFormRow>

                            <ModalFormRow label="Is this claim iro administration costs/charges?">
                                <Checkbox name="isAdminCost" onChange={handleChange} size="small" sx={{ p: 0 }} />
                            </ModalFormRow>

                            <ModalFormRow label="Is this claim iro Income Tax payable to SARS?">
                                <Checkbox name="isIncomeTax" onChange={handleChange} size="small" sx={{ p: 0 }} />
                            </ModalFormRow>

                            <ModalFormRow label="Is this debt related to a fiduciary asset?">
                                <Checkbox name="isFiduciaryDebt" onChange={handleChange} size="small" sx={{ p: 0 }} />
                            </ModalFormRow>

                            <ModalFormRow label="Is it a debt covered by credit protection insurance?">
                                <Checkbox name="isInsuredDebt" onChange={handleChange} size="small" sx={{ p: 0 }} />
                            </ModalFormRow>

                            <Box sx={{ mt: 2 }}>
                                <ModalFormRow 
                                    label="Is it funeral/death-bed expenses?" 
                                    note="(ONLY IN ICOP MARRIAGES: to be added back ito Sect 4(a) of the Estate Duty Act)"
                                >
                                    <Checkbox name="isFuneralExpense" onChange={handleChange} size="small" sx={{ p: 0 }} />
                                </ModalFormRow>

                                <ModalFormRow 
                                    label="Is this death duty paid on foreign asset in another country?" 
                                    note="(To be decuted ito Sect 16(c) of the Estate Duty Act)"
                                >
                                    <Checkbox name="isForeignDuty" onChange={handleChange} size="small" sx={{ p: 0 }} />
                                </ModalFormRow>
                            </Box>
                        </Stack>
                    )}

                    {/* ================= TAB 2: L&D NOTES/OTHER ================= */}
                    {tabValue === 1 && (
        <Stack spacing={2}>
            <Box sx={{ position: 'relative' }}>
                <Typography variant="caption" sx={{ fontWeight: 500, color: '#333', mb: 1, display: 'block' }}>
                    Notes to be printed on the L&D account about this asset (e.g. a divesting clause):
                </Typography>
                <TextField 
                    name="notes"
                    onChange={handleChange}
                    fullWidth 
                    multiline 
                    rows={12} 
                    variant="outlined" 
                    sx={{ 
                        bgcolor: '#fffde7', // Matches image_0545e6 yellow highlight
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 0,
                            border: '1px solid #1976d2'
                        }
                    }} 
                />
                {/* Visual red arrow indicator from source */}
                <Typography 
                    variant="caption" 
                    sx={{ 
                        color: 'red', 
                        position: 'absolute', 
                        left: -12, 
                        top: 28, 
                        fontWeight: 'bold' 
                    }}
                >
                    »
                </Typography>
            </Box>

            <Stack spacing={1} sx={{ mt: 1 }}>
                <ModalFormRow label="Print above notes on the L&D a/c?">
                    <Checkbox name="printNotes" onChange={handleChange} size="small" sx={{ p: 0 }} />
                </ModalFormRow>

                <ModalFormRow label="Counter-claim for suretyship given by the deceased?">
                    <Checkbox name="isSuretyship" onChange={handleChange} size="small" sx={{ p: 0 }} />
                </ModalFormRow>

                <ModalFormRow label="Valuation of non-listed shares/members interest in CC?">
                    <Checkbox name="isValuationShares" onChange={handleChange} size="small" sx={{ p: 0 }} />
                </ModalFormRow>

                <ModalFormRow label="Claim to property donated ito Sect 56(1)(c) or (d) of Income Tax Act?">
                    <Checkbox name="isDonatedPropertyClaim" onChange={handleChange} size="small" sx={{ p: 0 }} />
                </ModalFormRow>
            </Stack>
        </Stack>
                    )}

                    {/* ================= TAB 3: ESTATE DUTY ================= */}
                    {tabValue === 2 && (
                        <>
        <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 2, display: 'block', textAlign: 'center' }}>
            For Liquidation and Distribution as well as Estate Duty purposes:
        </Typography>
        
        <Stack spacing={0.5}>
            <ModalFormRow label="Is this claim iro a bequest made to charity/fund?">
                <Stack direction="row" spacing={1} alignItems="center">
                    <Checkbox name="isCharityBequest" onChange={handleChange} size="small" sx={{ p: 0 }} />
                    <Typography variant="caption">(Including public educational and religious institutions)</Typography>
                </Stack>
            </ModalFormRow>

            <ModalFormRow label="Is this claim iro a donation to Deceased by its beneficiary?">
                <Checkbox name="isDonationToDeceased" onChange={handleChange} size="small" sx={{ p: 0 }} />
            </ModalFormRow>

            <ModalFormRow label="Is this claim AGAINST the estate for accrual?">
                <Stack direction="row" spacing={1} alignItems="center">
                    <Checkbox name="isAccrualClaim" onChange={handleChange} size="small" sx={{ p: 0 }} />
                    <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                        (In terms of Sect. 3 of Matrimonial Property Act 88/1984)
                    </Typography>
                </Stack>
            </ModalFormRow>

            <ModalFormRow label="Is it iro an inheritance/donation made by a foreigner?">
                <Stack direction="row" spacing={1} alignItems="center">
                    <Checkbox name="isForeignInheritance" onChange={handleChange} size="small" sx={{ p: 0 }} />
                    <Typography variant="caption">(Deductable ito Sect 4(e))</Typography>
                </Stack>
            </ModalFormRow>

            <ModalFormRow label="Is it iro of credits/debts/rights of action OUTSIDE SA?">
                <Stack direction="row" spacing={1} alignItems="center">
                    <Checkbox name="isForeignDebt" onChange={handleChange} size="small" sx={{ p: 0 }} />
                    <Typography variant="caption">(This includes off-shore investment accounts)</Typography>
                </Stack>
            </ModalFormRow>

            <ModalFormRow label="Is this claim iro a gratuity/benefit society award?">
                <Stack direction="row" spacing={1} alignItems="center">
                    <Checkbox name="isGratuity" onChange={handleChange} size="small" sx={{ p: 0 }} />
                    <Typography variant="caption">(SA residents - enforceable inside and outside RSA)</Typography>
                </Stack>
            </ModalFormRow>

            <ModalFormRow label="Is it goodwill/licence/patent/design/trade mark/copyright?">
                <Stack direction="row" spacing={1} alignItems="center">
                    <Checkbox name="isGoodwill" onChange={handleChange} size="small" sx={{ p: 0 }} />
                    <Typography variant="caption">(NOT registered in South Africa)</Typography>
                </Stack>
            </ModalFormRow>

            <ModalFormRow label="Is it an annuity accruing to the heir to whom it is awarded?">
                <Checkbox name="isAnnuity" onChange={handleChange} size="small" sx={{ p: 0 }} />
            </ModalFormRow>

            <ModalFormRow label="Period for which annuity will be enjoyed">
                <Stack direction="row" spacing={1} alignItems="center">
                    <TextField name="annuityPeriod" onChange={handleChange} defaultValue="0" size="small" sx={{ width: '60px' }} />
                    <Typography variant="caption">years</Typography>
                </Stack>
            </ModalFormRow>

            <ModalFormRow label="Annual value of this annuity (in figures)">
                <TextField name="annuityValue" onChange={handleChange} defaultValue="0.00" size="small" sx={{ width: '150px', '& .MuiInputBase-input': { textAlign: 'right' } }} />
            </ModalFormRow>

            <ModalFormRow label="Yield to be used for Estate Duty calculation">
                <Stack direction="row" spacing={1} alignItems="center">
                    <TextField name="yield" onChange={handleChange} defaultValue="12.000000" size="small" sx={{ width: '120px' }} />
                    <Typography variant="caption">%</Typography>
                </Stack>
            </ModalFormRow>

            <ModalFormRow label="Value ito Sec. 5 of the Estate Duty Act">
                <Stack direction="row" spacing={1} alignItems="center">
                    <TextField name="sec5Value" onChange={handleChange} defaultValue="0.00" size="small" sx={{ width: '120px', '& .MuiInputBase-input': { textAlign: 'right' } }} />
                    <Button variant="contained" size="small" sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', border: '1px solid #ccc' }}>Table A</Button>
                    <Button variant="contained" size="small" sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', border: '1px solid #ccc' }}>Table B</Button>
                </Stack>
            </ModalFormRow>

            <ModalFormRow label="How and when did deceased first acquire the annuity?">
                <TextField name="annuityAcquired" onChange={handleChange} fullWidth size="small" />
            </ModalFormRow>

            <ModalFormRow label="Is this claim iro a donation made mortis causa?">
                <Checkbox name="isDonationMortisCausa" onChange={handleChange} size="small" sx={{ p: 0 }} />
            </ModalFormRow>

            <ModalFormRow label="Is this claim iro a donation made by the Deceased?">
                <Stack direction="row" spacing={1} alignItems="center">
                    <Checkbox name="isDonationByDeceased" onChange={handleChange} size="small" sx={{ p: 0 }} />
                    <Typography variant="caption">(Excluding donations defined in Note 1 of the Rev. 267)</Typography>
                </Stack>
            </ModalFormRow>

            <ModalFormRow label="Selling price of non-listed shares/members interest in CC?">
                <TextField name="sellingPriceShares" onChange={handleChange} defaultValue="0.00" size="small" sx={{ width: '150px', '& .MuiInputBase-input': { textAlign: 'right' } }} />
            </ModalFormRow>

            <ModalFormRow label="Beneficiary becomes owner by nomination agreement?">
                <Checkbox name="isNominationAgreement" onChange={handleChange} size="small" sx={{ p: 0 }} />
            </ModalFormRow>
        </Stack>
                        </>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5', borderTop: '1px solid #e0e0e0' }}>
                <Button onClick={handleSave} variant="contained" color="success" size="small" startIcon={<span>✓</span>} sx={{ textTransform: 'none' }}>OK</Button>
                <Button onClick={onClose} variant="contained" color="inherit" size="small" startIcon={<span>X</span>} sx={{ bgcolor: '#e0e0e0', textTransform: 'none' }}>Cancel</Button>
            </DialogActions>
        </Dialog>
    );
}